import React, { useState, useEffect } from "react";
import { ReferenceList, ReferenceValueView, ReferenceValueSynonymView } from "@/entities/all";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { PlusCircle, Pencil, Trash2, X } from "lucide-react";

import ReferenceValuesTable from "./ReferenceValuesTable";
import CreateValueModal from "./CreateValueModal";
import DeleteValueDialog from "./DeleteValueDialog";
import EditReferenceListModal from "./EditReferenceListModal";
import DeleteReferenceListDialog from "./DeleteReferenceListDialog";
import ReferenceListDependencies from "./ReferenceListDependencies";
import ReferenceValueLinks from "./ReferenceValueLinks";
import PaginatedTable from "../ui/paginated-table";

export default function ReferenceListDetailsSheet({ listId, isOpen, onClose, onSaved }) {
  const [referenceList, setReferenceList] = useState(null);
  const [values, setValues] = useState([]);
  const [synonyms, setSynonyms] = useState([]);
  const [filteredValues, setFilteredValues] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(null);
  const [isEditListModalOpen, setIsEditListModalOpen] = useState(false);
  const [isDeleteListDialogOpen, setIsDeleteListDialogOpen] = useState(false);
  const [activeDep, setActiveDep] = useState(null);

  useEffect(() => {
    if (listId && isOpen) { loadData(); setSearchTerm(''); setActiveDep(null); }
    if (!isOpen) { setReferenceList(null); setValues([]); setSynonyms([]); setFilteredValues([]); setActiveDep(null); }
  }, [listId, isOpen]);

  useEffect(() => { filterValues(); }, [values, synonyms, searchTerm]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [listData, valuesData, synonymsData] = await Promise.all([
        ReferenceList.filter({ id: listId }).then(lists => lists[0]),
        ReferenceValueView.filter({ list_id: listId }, 'canonical_value'),
        ReferenceValueSynonymView.filter({ list_id: listId }),
      ]);
      setReferenceList(listData);
      setValues(valuesData);
      setSynonyms(synonymsData);
    } catch (error) {
      console.error('Error loading reference list data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterValues = () => {
    if (!searchTerm) { setFilteredValues(values); return; }
    const term = searchTerm.toLowerCase();
    setFilteredValues(values.filter(value => {
      if (value.canonical_value.toLowerCase().includes(term)) return true;
      if (value.value_description?.toLowerCase().includes(term)) return true;
      return synonyms.filter(s => s.reference_value_id === value.id).some(s => s.synonym?.toLowerCase().includes(term));
    }));
  };

  const handleValueCreated = () => { loadData(); onSaved?.(); setIsCreateModalOpen(false); };
  const handleDeleteValue = (value) => { setSelectedValue(value); setIsDeleteDialogOpen(true); };
  const handleValueDeleted = () => { loadData(); onSaved?.(); setIsDeleteDialogOpen(false); setSelectedValue(null); };
  const getValueSynonyms = (valueId) => synonyms.filter(s => s.reference_value_id === valueId);

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
        <SheetContent side="right" className="w-full sm:max-w-[75vw] overflow-y-auto p-0 bg-page-bg border-l-0 shadow-[-20px_0_60px_0_var(--shadow-blue)] [&>button]:hidden">

          {/* Close button */}
          <div className="px-4 md:px-[60px] pt-[30px]">
            <button
              onClick={onClose}
              className="flex items-center gap-1 text-sm cursor-pointer bg-transparent border-0 p-0 text-nav-active hover:text-nav-active-hover transition-colors"
            >
              <X size={16} />
              Close
            </button>
          </div>

          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3 px-4 md:px-[60px] pt-4 pb-5">
            <div className="flex flex-col gap-1.5">
              <h2 className="text-[32px] font-medium leading-tight text-page-title">
                {isLoading ? '...' : referenceList?.list_name}
              </h2>
              {referenceList?.description && (
                <p className="text-sm text-page-muted">{referenceList.description}</p>
              )}
              {referenceList && (
                <div className="flex flex-wrap items-center gap-3 mt-1">
                  <span className="text-sm font-medium text-[#377AD6]">{values.length} values</span>
                  <span className="text-sm font-medium text-[#377AD6]">{synonyms.length} synonyms</span>
                  {referenceList.fuzzy_match_enabled && (
                    <span className="inline-flex items-center h-[18px] rounded-[9px] px-2 bg-[#377AD6]/10 text-xs font-medium text-[#377AD6]">
                      Fuzzy {referenceList.fuzzy_match_threshold}%
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <button
                onClick={() => setIsDeleteListDialogOpen(true)}
                className="flex items-center gap-2 h-9 px-4 rounded-[5px] border-2 border-red-300 text-red-500 bg-card-bg text-sm font-medium cursor-pointer hover:bg-red-500/10 transition-colors"
              >
                <Trash2 size={20} strokeWidth={2.5} />
                Delete
              </button>
              <button
                onClick={() => setIsEditListModalOpen(true)}
                className="flex items-center gap-2 h-9 px-4 rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg text-sm font-medium cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
              >
                <Pencil size={20} strokeWidth={2.5} />
                Edit
              </button>
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="flex items-center gap-2 h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer hover:bg-nav-active-dark transition-colors"
              >
                <PlusCircle size={24} />
                Add Value
              </button>
            </div>
          </div>

          <div className="px-4 md:px-[60px] pb-24 md:pb-[30px] flex flex-col gap-5">
            {/* Dependencies */}
            <ReferenceListDependencies
              listId={listId}
              listName={referenceList?.list_name}
              onManageLinks={(depId) => setActiveDep(depId)}
            />

            {activeDep && (
              <ReferenceValueLinks depId={activeDep} listId={listId} />
            )}

            {/* Search */}
            <div className="flex items-center h-9 bg-card-bg rounded-[18px] px-3 gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="shrink-0">
                <path d="M21 21L17.5001 17.5M20 11.5C20 16.1944 16.1944 20 11.5 20C6.80558 20 3 16.1944 3 11.5C3 6.80558 6.80558 3 11.5 3C16.1944 3 20 6.80558 20 11.5Z" stroke="#377AD6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input
                placeholder="Search values, descriptions, and synonyms..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 bg-transparent border-0 outline-none text-sm text-page-text placeholder:text-page-muted"
              />
            </div>

            {/* Values Table */}
            <PaginatedTable
              data={filteredValues}
              externalLoading={isLoading}
              renderTable={(paginatedData, loading) => (
                <div className="bg-card-bg rounded-[8px] overflow-x-auto">
                  <ReferenceValuesTable
                    values={paginatedData}
                    synonyms={synonyms}
                    isLoading={loading}
                    listId={listId}
                    onValueUpdate={() => { loadData(); onSaved?.(); }}
                    getValueSynonyms={getValueSynonyms}
                    onDeleteValue={handleDeleteValue}
                  />
                </div>
              )}
            />
          </div>
        </SheetContent>
      </Sheet>

      <CreateValueModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onValueCreated={handleValueCreated}
        listId={listId}
      />

      {selectedValue && (
        <DeleteValueDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => { setIsDeleteDialogOpen(false); setSelectedValue(null); }}
          onValueDeleted={handleValueDeleted}
          value={selectedValue}
        />
      )}

      <EditReferenceListModal
        isOpen={isEditListModalOpen}
        onClose={() => setIsEditListModalOpen(false)}
        onListUpdated={() => { setIsEditListModalOpen(false); loadData(); onSaved?.(); }}
        list={referenceList}
      />

      <DeleteReferenceListDialog
        isOpen={isDeleteListDialogOpen}
        onClose={() => setIsDeleteListDialogOpen(false)}
        onListDeleted={() => { onClose(); onSaved?.(); }}
        list={referenceList}
      />
    </>
  );
}
